import { inject } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';

import { switchMap, tap, throwError } from 'rxjs';
import { environment } from '@fb/environments';
import { AuthMethod } from '@fb/models';

import { AuthService } from './auth.service';

/**
 * Extra time to be sure that request won't go to the server where token is already expired
 */
const extraTime = 10000;

interface RefreshResponse {
  access: string;
  permissions: string[];
}

/**
 * Refresh token interceptor
 */
export function refreshTokenInterceptor(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
) {
  const authService = inject(AuthService);
  const httpBackend = inject(HttpBackend);
  const refreshHttpClient = new HttpClient(httpBackend);

  const signinReq = request.url.indexOf('auth') >= 0;
  const assetsI18nReq = request.url.indexOf('assets/i18n') >= 0;

  if (environment.useMocks || signinReq || assetsI18nReq) {
    return next(request);
  }

  const authToken = authService.getAuthToken();
  const refreshToken = authService.getRefreshToken();

  if (authToken && refreshToken) {
    const now = new Date().getTime();
    let authData;
    let refreshData;

    try {
      authData = authService.decodeJwt(authToken);
      refreshData = authService.decodeJwt(refreshToken);
    } catch (e) {
      return throwError(() => new Error('Auth or refresh token is incorrect'));
    }

    if ((<any>refreshData).exp * 1000 < now + extraTime) {
      return throwError(() => new Error('Refresh token is expired'));
    } else if ((<any>authData).exp * 1000 < now + extraTime) {
      return refreshHttpClient
        .post<RefreshResponse>(
          `${environment.apiHost}auth/refresh/`,
          {
            refresh: refreshToken,
          },
          {
            withCredentials: true,
          },
        )
        .pipe(
          tap((response) =>
            authService.setAuthToken(AuthMethod.TOKEN, response.access),
          ),
          switchMap(() => next(request)),
        );
    } else {
      return next(request);
    }
  }

  return next(request);
}
